/* eslint-disable @typescript-eslint/no-explicit-any */
import { Container } from 'ui';
import { CurrencyContext, IWithContextProps, core, withContext } from '@tock/coretypes';
import { PrevisitQuestions } from 'components/business/receipts/PrevisitQuestions';
import { PurchaseCancellationPolicyDescription } from 'components/business/receipts/PurchaseCancellationPolicyDescription';
import { Receipt } from 'components/business/receipts/Receipt';
import { ReceiptCallouts } from './ReceiptCallouts';
import { ReceiptMessage } from 'components/business/receipts/ReceiptMessage';
import { ReceiptMobileTicket } from 'components/business/receipts/MobileTicket/ReceiptMobileTicket';
import { ReceiptOfferingSummary } from 'components/business/receipts/OfferingSummary/ReceiptOfferingSummary';
import { Route, RouteComponentProps } from 'react-router-dom';
import { Typography } from 'omakase';
import { isOrderVariety } from '@tock/shared/utils/offerings';
import { joinPath } from 'utils/pathUtils';
import { parse, stringify } from '@tock/query-string';
import AccountBackNav from 'components/AccountBackNav';
import BallPulse from '@tock/shared/components/BallPulse';
import BaseComponent from '@tock/shared/components/BaseComponent';
import CancelBookingRoute from 'routes/protected/account/CancelBookingRoute';
import ReceiptLocationPanel from 'components/business/receipts/ReceiptLocationPanel';
import ReservationReceipt from 'components/business/receipts/ReservationReceipt';
import ReservationSurveyRoute from 'routes/business/ReservationSurveyRoute';
import ResyCreditBanner from 'components/business/landing-page/ResyCreditBanner';
import SectionHeader from 'components/SectionHeader';
import _ from 'lodash';
import connect, { ActionsType, DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

interface MatchParams {
  purchaseId?: string;
}

interface ConnectProps extends DefaultConnectProps {
  actions: ActionsType;
  business: StoreType['business'];
  pastPurchase: StoreType['pastPurchase'];
  ui: StoreType['ui'];
}

type AllProps = RouteComponentProps<MatchParams> & ConnectProps & IWithContextProps;

class ProfileReceiptRoute extends BaseComponent<AllProps> {
  componentDidMount() {
    const { actions } = this.props;

    const ReservationHeaderDetails = {
      hasBack: true,
      hasLogo: false,
    };
    actions.navigation.set(ReservationHeaderDetails);
    this.fetchData();
  }

  componentDidUpdate(prevProps: AllProps) {
    const { location: prevLocation } = prevProps;
    const { location } = this.props;

    if (_.includes(prevLocation.pathname, 'cancel') && !_.includes(location.pathname, 'cancel')) {
      this.fetchData();
    }
  }

  redirectToSurveyIfNeeded(prevProps: AllProps) {
    const { ticketTypeDetails: prevTicketTypeDetails } = prevProps.pastPurchase;
    const { ticketTypeDetails, pastPurchase } = this.props.pastPurchase;
    const purchase = this.getFirstPurchase(pastPurchase);
    if (
      !prevTicketTypeDetails &&
      ticketTypeDetails?.shouldPromptSurvey &&
      purchase.question.length === 0
    ) {
      this.handleContinueToQuestions();
    }
  }

  componentWillUnmount() {
    const { actions } = this.props;
    actions.purchase.clearPastPurchase();
    actions.purchase.clearPastPurchaseTicketTypeDetails();
    actions.business.clearBusiness();
  }

  fetchData() {
    const {
      actions,
      location: { search },
    } = this.props;
    const query = parse(search);

    const purchaseId = query.purchaseId || this.props.match.params.purchaseId;
    const purchaseIds = query.purchaseIds;
    let promise: any;
    if (purchaseId) {
      promise = actions.purchase.fetchPastPurchase({ purchaseId }).then(({ payload }) => {
        const ticketTypeId = payload.ticketTypeId;
        actions.purchase.fetchPastPurchaseTicketTypeDetails(ticketTypeId);
        actions.business.fetch(payload.business.domainName);
      });
    } else if (purchaseIds) {
      promise = Promise.all(
        _.map(_.split(purchaseIds, ','), (purchaseId) => {
          return actions.purchase.fetchPastPurchase({ purchaseId });
        })
      );
    } else if (query.token) {
      promise = actions.purchase
        .fetchPastPurchase({ purchaseId, token: query.token })
        .then((pastPurchase: any) => {
          const ticketTypeId = pastPurchase.payload.ticketTypeId;
          actions.purchase.fetchPastPurchaseTicketTypeDetails(ticketTypeId);
        });
    } else {
      promise = Promise.reject(null);
    }
    promise.catch(() => {
      this.props.routeTo('/');
    });
  }

  getFirstPurchase(pastPurchase: { [id: number]: core.PastPurchase }): core.PastPurchase {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return _.first(_.values(pastPurchase))!;
  }

  getLastPurchase(pastPurchase: { [id: number]: core.PastPurchase }): core.PastPurchase {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return _.last(_.values(pastPurchase))!;
  }

  getContactRestaurantLink() {
    const {
      pastPurchase: { pastPurchase },
    } = this.props;
    const purchase = this.getFirstPurchase(pastPurchase);
    if (purchase.business && purchase.business.email) {
      const resturantLink: string[] = [];
      resturantLink.push(
        `mailto:${purchase.business.email}?subject=Inquiry for ${purchase.ticketTypeName}`
      );
      if (purchase.business.name) {
        resturantLink.push(`at ${purchase.business.name}`);
      }
      resturantLink.push(`on ${purchase.ticketDateTime}`);
      return _.join(resturantLink, ` `);
    }
    return '';
  }

  handleTransfer() {
    const {
      pastPurchase: { pastPurchase },
    } = this.props;
    const purchase = this.getFirstPurchase(pastPurchase);
    this.props.actions.transfer.setTransferInfo(purchase);
    this.props.history.push('/profile/start-transfer/option');
  }

  handleModify() {
    const {
      history,
      location: { search },
      pastPurchase: { pastPurchase },
    } = this.props;
    const { token } = parse(search);
    const purchase = this.getLastPurchase(pastPurchase);
    this.props.actions.reschedule.setRescheduleInfo(purchase, token);
    history.push(`/${purchase?.business?.domainName}/reschedule/start`);
  }

  handleCancelTransfer() {
    const {
      pastPurchase: { pastPurchase },
    } = this.props;
    const purchase = this.getFirstPurchase(pastPurchase);
    this.props.actions.transfer.cancel(purchase.id).then(this.fetchData);
  }

  handleShowCancelBooking() {
    const {
      match: { url },
    } = this.props;
    this.props.history.push(joinPath(url, '/cancel'));
  }

  handleContinueToQuestions() {
    const {
      location: { search },
      match: { params },
    } = this.props;
    const { purchaseId } = parse(search);
    this.props.history.replace(`/profile/reservations/${purchaseId || params.purchaseId}/survey`);
  }

  onClickExitMobileNav() {
    this.props.history.push('/profile/reservations');
  }

  render() {
    const {
      app,
      business: { business },
      match: { path },
      pastPurchase,
    } = this.props;
    const showCancel = _.includes(this.props.location.pathname, 'cancel');

    if (
      _.isEmpty(pastPurchase.pastPurchase) ||
      app.configRequestInProgress ||
      pastPurchase.requestInProgress ||
      this.props.business.requestInProgress
    ) {
      return (
        <div className="MainContent">
          <div className="SectionWrapper">
            <div className="SectionContent">
              <AccountBackNav />
              <SectionHeader modifierClassName="Account-header" heading="Reservation details" />
              <div className="Consumer-genericLoading">
                <BallPulse position="INLINE" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (showCancel) {
      return (
        <div className="SectionWrapper">
          <div className="SectionContent">
            <Route path={`${path}/cancel`} component={CancelBookingRoute} />
          </div>
        </div>
      );
    }

    const purchase = this.getLastPurchase(pastPurchase.pastPurchase);
    let mobileTicketUrl: string | undefined;
    if (purchase.ticketType?.isUsingMobileTicket) {
      const search = purchase.mobileTicketToken
        ? `?${stringify({ token: purchase.mobileTicketToken })}`
        : '';
      mobileTicketUrl = `${app.settings?.EXTERNAL_API_BASE}/consumer/purchase/mobile-ticket-pdf/${purchase.id}${search}`;
    }
    const orderOrReservation = isOrderVariety(purchase.ticketTypeVariety) ? 'Order' : 'Reservation';

    return (
      <CurrencyContext.Provider value={purchase.business?.currencyCode || 'USD'}>
        <Container>
          <AccountBackNav />
          <Route path={`${path}/survey`} component={ReservationSurveyRoute} />
          <Route path={`${path}/cancel`} component={CancelBookingRoute} />
          <Receipt
            sectionHeader={
              <>
                <Typography variant="heading03" data-testid="reservation-title">
                  {orderOrReservation} details
                </Typography>
                <Typography variant="caption" color="fgSubtle">
                  Confirmation: {purchase.confirmationId}
                </Typography>
              </>
            }
            reservationSummary={
              business && (
                <ReceiptOfferingSummary
                  purchase={purchase}
                  business={business}
                  fetchData={this.fetchData}
                  isAuthenticated
                  cancellationPolicy={
                    <PurchaseCancellationPolicyDescription
                      businessName={purchase.business?.name ?? ''}
                      description={purchase.cancellationPolicyDescription}
                      paymentCardHold={purchase.paymentCardHold || undefined}
                    />
                  }
                />
              )
            }
            reservationDetails={
              business && (
                <ReservationReceipt
                  isAuthenticated
                  isPatronAuthTypesEmpty={
                    _.isArray(app.patronAuthTypes) && _.isEmpty(app.patronAuthTypes)
                  }
                  mobileTicketUrl={mobileTicketUrl}
                  onContinueToQuestions={this.handleContinueToQuestions}
                  onShowCancelBooking={this.handleShowCancelBooking}
                  onTransfer={this.handleTransfer}
                  pastPurchase={pastPurchase}
                  inVisitEdit={false}
                  business={business}
                />
              )
            }
            messageFromBusiness={
              <ReceiptMessage
                purchase={purchase}
                business={business}
                confirmationText={pastPurchase.ticketTypeDetails?.confirmationText}
              />
            }
            resyCreditBanner={
              <ResyCreditBanner isResyCreditEligible={!!business?.isResyCreditEligible} />
            }
            previsitQuestions={
              <PrevisitQuestions
                purchase={purchase}
                questions={pastPurchase.questions}
                ticketTypeDetails={pastPurchase.ticketTypeDetails}
                handleContinueToQuestions={this.handleContinueToQuestions}
              />
            }
            callout={
              <ReceiptCallouts
                purchase={purchase}
                businessName={purchase.business?.name ?? ''}
                ticketType={purchase.ticketType || undefined}
              />
            }
            mobileTicket={<ReceiptMobileTicket purchase={purchase} />}
            rightColumn={
              business && (
                <ReceiptLocationPanel
                  business={business}
                  ticketTypeDetails={pastPurchase.ticketTypeDetails}
                  deliveryLocation={purchase.delivery?.location || undefined}
                />
              )
            }
          />
        </Container>
      </CurrencyContext.Provider>
    );
  }
}

export default connect<ConnectProps & RouteComponentProps<MatchParams>>(
  withContext(ProfileReceiptRoute),
  ['business', 'pastPurchase', 'ui']
);
